import React from 'react';
import Layout from '../components/Layout';

const Privacy: React.FC = () => {
  return (
    <Layout title="Privacy Policy" subtitle="Effective Date: 28 August 2024">
      <div className="space-y-12">
        {/* Introduction */}
        <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
          <p className="text-white/90 mb-6 leading-relaxed">
            At Incupie Technologies Private Limited ("Incupie"), we are committed to protecting 
            your privacy and ensuring that your personal information is handled responsibly. 
            This Privacy Policy outlines how we collect, use, disclose, and protect your 
            information when you use our digital services related to AI and data compression.
          </p>
          <p className="text-white/90 leading-relaxed">
            By using Incupie's services, you agree to the collection and use of your information 
            as described in this Privacy Policy. If you do not agree with the terms of this 
            policy, please refrain from using our services.
          </p>
        </div>

        {/* Section 1: Information We Collect */}
        <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-6">1. Information We Collect</h2>
          <p className="text-white/90 mb-6">
            Incupie collects different types of information to provide and improve our services. 
            This information may include:
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-white mb-3">1.1 Personal Information</h3>
              <p className="text-white/90 mb-4">
                When you register for an account, subscribe to our services, or communicate with 
                us, we may collect personal information such as:
              </p>
              <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
                <li>Name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Billing and payment information</li>
                <li>Company or organization details</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-3">1.2 Technical Information</h3>
              <p className="text-white/90 mb-4">
                When you use our services, we may collect certain technical information 
                automatically, including:
              </p>
              <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
                <li>IP address</li>
                <li>Device and browser information</li>
                <li>Operating system</li>
                <li>Usage data, such as log files and performance data related to our AI and compression services</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-3">1.3 Uploaded Content</h3>
              <p className="text-white/90">
                As part of our AI and compression services, you may upload data, files, or other 
                content to our platform. This content is processed to deliver the service and 
                is governed by our security and confidentiality policies.
              </p>
            </div>
          </div>
        </div>

        {/* Section 2: How We Use Your Information */}
        <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-6">2. How We Use Your Information</h2>
          <p className="text-white/90 mb-6">We use the information we collect for the following purposes:</p>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-white mb-3">2.1 Service Delivery</h3>
              <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
                <li>To provide, operate, and maintain our AI and compression services</li>
                <li>To process your transactions and manage your subscriptions</li>
                <li>To offer technical support and respond to your inquiries</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-3">2.2 Service Improvement</h3>
              <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
                <li>To analyze usage patterns and improve the performance and features of our services</li>
                <li>To develop new products, services, and technologies related to AI and data compression</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-3">2.3 Communication</h3>
              <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
                <li>To send you important updates, including service notifications, security alerts, or changes to our policies</li>
                <li>To provide you with marketing and promotional materials related to Incupie services, subject to your consent or preferences</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 3: Sharing and Disclosure */}
        <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-6">3. Sharing and Disclosure of Information</h2>
          <p className="text-white/90 mb-6">
            Incupie does not sell your personal information. However, we may share or disclose 
            your information under the following circumstances:
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold text-white mb-3">3.1 Service Providers</h3>
              <p className="text-white/90">
                We may share your information with trusted third-party service providers who assist 
                us in operating our business, such as payment processors, hosting providers, and 
                customer support services. These third parties are bound by confidentiality agreements 
                and may only use your information for purposes specified by Incupie.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-3">3.2 Legal Compliance</h3>
              <p className="text-white/90">
                We may disclose your information to comply with legal obligations, such as responding 
                to a subpoena, court order, or legal process, or to protect our rights, property, 
                or the safety of our users and the public.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-white mb-3">3.3 Business Transfers</h3>
              <p className="text-white/90">
                In the event of a merger, acquisition, or sale of all or part of our business, 
                your information may be transferred to the acquiring entity. You will be notified 
                of any such transfer, and your information will remain protected under this Privacy Policy.
              </p>
            </div>
          </div>
        </div>

        {/* Section 4: Data Security */}
        <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-6">4. Data Security</h2>
          <p className="text-white/90 mb-4">
            We take the security of your information seriously and implement reasonable measures 
            to protect your data from unauthorized access, disclosure, alteration, or destruction. 
            These measures include encryption, access controls, and regular security audits.
          </p>
          <p className="text-white/90">
            However, no method of data transmission over the internet or method of electronic 
            storage is completely secure. While we strive to protect your personal information, 
            we cannot guarantee its absolute security.
          </p>
        </div>

        {/* Section 5: Data Retention */}
        <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-6">5. Data Retention</h2>
          <p className="text-white/90 mb-4">
            We retain your personal information only as long as necessary to fulfill the purposes 
            outlined in this Privacy Policy or as required by law. Once your information is no 
            longer needed, we will securely delete or anonymize it.
          </p>
          <p className="text-white/90">
            Data related to AI and compression processing, such as uploaded files and processing 
            results, are typically retained for a limited time based on your subscription plan 
            or service usage. You may request the deletion of such data after the service has 
            been rendered, subject to our retention policies.
          </p>
        </div>

        {/* Section 6: Your Rights */}
        <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-6">6. Your Rights</h2>
          <p className="text-white/90 mb-4">You have the following rights concerning your personal information:</p>
          <ul className="list-disc list-inside text-white/80 space-y-3 ml-4 mb-6">
            <li><strong className="text-white">Access and Correction:</strong> You may request access to your personal information and correct any inaccuracies</li>
            <li><strong className="text-white">Deletion:</strong> You may request that we delete your personal information, subject to certain legal or contractual obligations</li>
            <li><strong className="text-white">Opt-Out:</strong> You can opt out of receiving marketing and promotional communications from us by following the instructions in the emails or contacting us directly</li>
            <li><strong className="text-white">Data Portability:</strong> You may request a copy of your personal information in a structured, machine-readable format</li>
          </ul>
          <p className="text-white/90">
            To exercise any of these rights, please contact us at{' '}
            <a href="mailto:support@incupie.com" className="text-primary-400 hover:text-primary-300 transition-colors">
              support@incupie.com
            </a>. 
            We will respond to your request within a reasonable timeframe.
          </p>
        </div>

        {/* Remaining Sections */}
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6">7. Cookies and Tracking Technologies</h2>
            <p className="text-white/90 mb-4">
              We use cookies and similar tracking technologies to enhance your experience with 
              our services, such as remembering your preferences, analyzing usage patterns, 
              and delivering targeted content.
            </p>
            <p className="text-white/90">
              You can manage your cookie preferences through your browser settings. However, 
              disabling cookies may affect the functionality of our services.
            </p>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6">8. Third-Party Links</h2>
            <p className="text-white/90">
              Our services may contain links to third-party websites or services that are not 
              controlled by Incupie. We are not responsible for the privacy practices or content 
              of these third parties. We encourage you to review their privacy policies before 
              providing any personal information.
            </p>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6">9. Children's Privacy</h2>
            <p className="text-white/90">
              Our services are not intended for individuals under the age of 18. We do not 
              knowingly collect personal information from children. If we discover that a child 
              has provided us with personal information, we will promptly delete such information. 
              If you believe that we may have collected information from a child, please contact 
              us at{' '}
              <a href="mailto:support@incupie.com" className="text-primary-400 hover:text-primary-300 transition-colors">
                support@incupie.com
              </a>.
            </p>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6">10. Changes to This Privacy Policy</h2>
            <p className="text-white/90">
              We may update this Privacy Policy from time to time to reflect changes in our 
              practices, technologies, or legal requirements. Any changes will be posted on our 
              website with the updated effective date. We encourage you to review this policy 
              periodically to stay informed about how we are protecting your information.
            </p>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gradient-to-r from-primary-500/10 to-primary-600/10 border border-primary-500/20 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-6">11. Contact Us</h2>
          <p className="text-white/90 mb-6">
            If you have any questions or concerns about this Privacy Policy or how we handle 
            your information, please contact us at:
          </p>
          <div className="space-y-2 text-white/90 mb-6">
            <p><strong className="text-white">Email:</strong>{' '}
              <a href="mailto:support@incupie.com" className="text-primary-400 hover:text-primary-300 transition-colors">
                support@incupie.com
              </a>
            </p>
            <p><strong className="text-white">Phone:</strong> +91-9915080818</p>
            <p><strong className="text-white">Address:</strong><br />
               No. 19, 4th C Cross Koramangala Industrial,<br />
               5th Block, Area, Bengaluru,<br />
               Karnataka 560095, India</p>
          </div>
          <p className="text-white/90">
            By using Incupie's services, you acknowledge that you have read, understood, 
            and agree to be bound by this Privacy Policy.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default Privacy;
